import React, { useState } from 'react';
import Dialog from 'src/components/common/Dialog';
import Button from 'src/components/controls/forms/redesign/FormButton';
import { useForm } from 'react-hook-form';
import RHFSelect from 'src/components/controls/tempRHFControls/RHFSelect';
import RHFInputBox from 'src/components/controls/tempRHFControls/RHFInputBox';
import { AnyObject } from 'final-form';
import useFetch from 'use-http';

interface ITimeRptDialogProps {
    dialogName: string;
    handleReport?: (data: AnyObject) => void;
    hideDialog: () => void;
}

const labelStyle: React.CSSProperties = { whiteSpace: 'nowrap', width: '9rem' };

const downloadBlobResponseAsFile = (blob: Blob, fileName: string) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName || 'document';
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
};

// Port of the Oracle Forms "Landman Time Report" screen (timerpt.fmt) - a single SSRS report
// (TimeRpt.rdl) backed by usp_TimeRptDetail/usp_TimeRptSummary, same _ssrsClient.DownloadReportAsync
// pattern as InvoiceOfficeBreakdownDialog.tsx. Beginning/Ending Period are pay-period numbers
// (e.g. 202601), not dates - matches PAY_PERIODS.period_no, not a calendar date field.
const TimeRptDialog = (props: ITimeRptDialogProps) => {
    const { register, errors, getValues } = useForm();
    const { post, response } = useFetch();
    const [loading, setLoading] = useState<boolean>(false);

    const OKClicked = async () => {
        const data = getValues();
        const reportOptions = data?.reportOptions;

        setLoading(true);

        try {
            await post('/v1.0/reports/time-rpt', {
                begPeriod: reportOptions?.begPeriod,
                endPeriod: reportOptions?.endPeriod,
                include90: reportOptions?.include90 || 'Y',
                empType: reportOptions?.empType || 'ALL',
                office: reportOptions?.office || 'ALL',
                clientNumber: reportOptions?.clientNumber || ''
            });

            if (response.ok) {
                const blob = await response.blob();
                downloadBlobResponseAsFile(blob, 'TimeRpt.pdf');
            }
        } catch (e) {
            console.log('Unable to generate Landman Time report.', e);
        }

        setLoading(false);
        props.handleReport?.(reportOptions);
        props.hideDialog();
    };

    return (
        <Dialog title="Build Landman Time Report" dialogName={props.dialogName} isExitButtonInvisible={true} bodyStyle={{ width: '30%' }}>
            <div style={{ display: 'flex', justifyContent: 'center' }}>
                <form style={{ width: '90%' }}>
                    <RHFInputBox
                        label="Beginning Period"
                        labelOnLeft={true}
                        labelStyle={labelStyle}
                        name="reportOptions.begPeriod"
                        register={register}
                        errors={errors}
                        style={{ width: '100%' }}
                        bodyStyle={{ width: '100%' }}
                    />
                    <RHFInputBox
                        label="Ending Period"
                        labelOnLeft={true}
                        labelStyle={labelStyle}
                        name="reportOptions.endPeriod"
                        register={register}
                        errors={errors}
                        style={{ width: '100%' }}
                        bodyStyle={{ width: '100%' }}
                    />
                    <RHFSelect
                        label="Employee-Contractor?"
                        labelOnLeft={true}
                        labelStyle={labelStyle}
                        name="reportOptions.empType"
                        options={[
                            { value: 'ALL', label: 'ALL' },
                            { value: 'E', label: 'Employees' },
                            { value: 'C', label: 'Contractors' },
                            { value: 'CS', label: 'Contractors and Sub-Contractors' },
                            { value: 'S', label: 'Sub-Contractors' },
                        ]}
                        register={register}
                        errors={errors}
                        defaultVal="ALL"
                        style={{ width: '100%' }}
                        bodyStyle={{ width: '100%' }}
                    />
                    <RHFSelect
                        label="Office"
                        labelOnLeft={true}
                        labelStyle={labelStyle}
                        name="reportOptions.office"
                        options={[
                            { value: 'ALL', label: 'ALL' },
                            { value: 'CO', label: 'Colorado' },
                            { value: 'OK', label: 'Oklahoma' },
                            { value: 'TX', label: 'Texas' },
                            { value: 'AR', label: 'Arkansas' },
                            { value: 'WV', label: 'West Virginia' },
                            { value: 'FW', label: 'Fort Worth' },
                            { value: 'NA', label: 'Nacogdoches' },
                            { value: 'PA', label: 'Pennsylvania' },
                        ]}
                        register={register}
                        errors={errors}
                        defaultVal="ALL"
                        style={{ width: '100%' }}
                        bodyStyle={{ width: '100%' }}
                    />
                    <RHFInputBox
                        label="Client No."
                        labelOnLeft={true}
                        labelStyle={labelStyle}
                        name="reportOptions.clientNumber"
                        register={register}
                        errors={errors}
                        style={{ width: '100%' }}
                        bodyStyle={{ width: '100%' }}
                    />
                    <RHFSelect
                        label="Include Client 90?"
                        labelOnLeft={true}
                        labelStyle={labelStyle}
                        name="reportOptions.include90"
                        options={[
                            { value: 'Y', label: 'Yes' },
                            { value: 'N', label: 'No' },
                        ]}
                        register={register}
                        errors={errors}
                        defaultVal="Y"
                        style={{ width: '100%' }}
                        bodyStyle={{ width: '100%' }}
                    />
                </form>
            </div>
            <div className="form-row" style={{ marginTop: '2rem', justifyContent: 'center' }}>
                <Button
                    className="blue-hover-button"
                    style={{ width: '18%', marginRight: '1rem' }}
                    text={loading ? 'Generating' : 'OK'}
                    disabled={loading}
                    loading={loading}
                    onClick={() => !loading && OKClicked()}
                />
                <Button
                    className="blue-hover-button"
                    style={{ width: '18%', marginRight: '1rem' }}
                    text={'Cancel'}
                    disabled={loading}
                    onClick={() => props.hideDialog()}
                />
            </div>
        </Dialog>
    );
};

export default TimeRptDialog;



// WEBPACK FOOTER //
// ./src/components/reports/dialogs/TimeRptDialog.tsx